import { motion, AnimatePresence } from "framer-motion";
import { X, RefreshCw, Plus, Minus, ShoppingBag, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { useStoreSettings } from "../context/StoreSettingsContext";
import { useAuth } from "../context/AuthContext";
import { WishlistButton } from "./WishlistButton";
import { Stars } from "./Stars";
import api from "../lib/api";

const euro = (n) => `€${n.toFixed(2).replace(".", ",")}`;

/* Lightbox a tutto schermo: frecce + swipe + chiusura */
const ImageLightbox = ({ images, index, onClose, onNav }) => {
  const touchX = useRef(null);
  const prev = () => onNav((index - 1 + images.length) % images.length);
  const next = () => onNav((index + 1) % images.length);
  return (
    <div
      className="safe-pt safe-pb fixed inset-0 z-[110] flex items-center justify-center bg-black/95 p-4"
      onClick={onClose}
      data-testid="image-lightbox"
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 40) (dx > 0 ? prev : next)();
        touchX.current = null;
      }}
    >
      <button onClick={onClose} data-testid="close-lightbox" className="absolute right-4 top-4 z-10 grid h-10 w-10 place-items-center rounded-full bg-cream/90 text-ink shadow-lg" style={{ top: "calc(env(safe-area-inset-top, 0px) + 1rem)" }}>
        <X className="h-5 w-5" strokeWidth={1.8} />
      </button>
      {images.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="lightbox-prev" className="absolute left-3 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-cream/90 text-ink shadow-lg">
            <ChevronLeft className="h-5 w-5" strokeWidth={2} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} data-testid="lightbox-next" className="absolute right-3 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-cream/90 text-ink shadow-lg">
            <ChevronRight className="h-5 w-5" strokeWidth={2} />
          </button>
        </>
      )}
      <img src={images[index]} alt="" onClick={(e) => e.stopPropagation()} className="max-h-full max-w-full rounded-xl object-contain" data-testid="lightbox-image" />
    </div>
  );
};

export const ProductModal = ({ product, onClose }) => {
  const { addItem } = useCart();
  const { canShop } = useStoreSettings();
  const { user } = useAuth();
  const [qty, setQty] = useState(1);
  const [reviews, setReviews] = useState([]);
  const [myStars, setMyStars] = useState(5);
  const [comment, setComment] = useState("");
  const [avg, setAvg] = useState({ rating: 0, count: 0 });
  const [currentImg, setCurrentImg] = useState(null);
  const [lightbox, setLightbox] = useState(false);
  const [descOpen, setDescOpen] = useState(false);
  const touchX = useRef(null);
  const gallery = product ? [...new Set([product.image, ...(product.images || [])].filter(Boolean))] : [];
  const imgIndex = Math.max(0, gallery.indexOf(currentImg));
  const navImg = (dir) => {
    if (gallery.length < 2) return;
    const next = (imgIndex + dir + gallery.length) % gallery.length;
    setCurrentImg(gallery[next]);
  };

  useEffect(() => {
    setCurrentImg(product?.image || null);
  }, [product]);
  const [reco, setReco] = useState([]);

  useEffect(() => {
    if (product) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [product]);

  useEffect(() => {
    setQty(1);
    if (product) {
      setAvg({ rating: product.rating || 0, count: product.rating_count || 0 });
      setComment("");
      api.get(`/products/${product.id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
      api.get(`/products/${product.id}/recommendations`).then((r) => setReco(r.data)).catch(() => setReco([]));
    }
  }, [product]);

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/products/${product.id}/reviews`, { stars: myStars, comment });
      setAvg({ rating: data.rating, count: data.rating_count });
      const r = await api.get(`/products/${product.id}/reviews`);
      setReviews(r.data);
      setComment("");
      toast.success("Grazie per la recensione!");
    } catch {
      toast.error("Devi accedere per recensire");
    }
  };

  return (
    <AnimatePresence>
      {product && (
        <>
          <div className="safe-pt safe-pb fixed inset-0 z-[90] flex items-center justify-center bg-ink/50 p-4 backdrop-blur-sm md:p-8" onClick={onClose} data-testid="modal-overlay">
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="grid max-h-[85vh] w-full max-w-4xl grid-cols-1 overflow-y-auto rounded-3xl bg-cream md:max-h-[90vh] md:grid-cols-2 md:overflow-hidden"
              data-testid="product-modal"
            >
              <div
                className="clip-frame relative h-[34vh] cursor-zoom-in md:h-full md:min-h-[420px]"
                onClick={() => setLightbox(true)}
                onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
                onTouchEnd={(e) => {
                  if (touchX.current === null) return;
                  const dx = e.changedTouches[0].clientX - touchX.current;
                  if (Math.abs(dx) > 40) navImg(dx > 0 ? -1 : 1);
                  touchX.current = null;
                }}
                data-testid="modal-image-area"
              >
                <img src={currentImg} alt={product.name} className="h-full w-full object-contain bg-white p-4" data-testid="modal-main-image" />
                {/* Icone sempre visibili sopra l'immagine: cuore + chiusura */}
                <div className="absolute right-3 top-3 z-10 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <WishlistButton product={product} className="border border-ink/10 bg-cream/90 shadow-md backdrop-blur" />
                  <button onClick={onClose} data-testid="close-modal-button" aria-label="Chiudi e continua gli acquisti" className="grid h-9 w-9 place-items-center rounded-full bg-cream/90 text-ink shadow-md backdrop-blur transition-colors hover:bg-cream">
                    <X className="h-5 w-5" strokeWidth={1.8} />
                  </button>
                </div>
                {gallery.length > 1 && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); navImg(-1); }}
                      data-testid="gallery-prev"
                      aria-label="Foto precedente"
                      className="absolute left-2 top-1/2 z-10 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full bg-cream/90 text-ink shadow-md backdrop-blur"
                    >
                      <ChevronLeft className="h-4 w-4" strokeWidth={2} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); navImg(1); }}
                      data-testid="gallery-next"
                      aria-label="Foto successiva"
                      className="absolute right-2 top-1/2 z-10 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full bg-cream/90 text-ink shadow-md backdrop-blur"
                    >
                      <ChevronRight className="h-4 w-4" strokeWidth={2} />
                    </button>
                  </>
                )}
                {gallery.length > 1 && (
                  <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5" onClick={(e) => e.stopPropagation()} data-testid="modal-gallery">
                    {gallery.map((url, i) => (
                      <button
                        key={url}
                        onClick={() => setCurrentImg(url)}
                        data-testid={`gallery-thumb-${i}`}
                        aria-label={`Foto ${i + 1}`}
                        className={`h-2.5 rounded-full transition-all ${url === currentImg ? "w-6 bg-sage" : "w-2.5 bg-cream/70 hover:bg-cream"}`}
                      />
                    ))}
                  </div>
                )}
                {product.recurring && (
                  <span className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-cream/90 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-electric-deep shadow-md backdrop-blur">
                    <RefreshCw className="h-3.5 w-3.5" strokeWidth={2} /> Essenziale mensile
                  </span>
                )}
              </div>

              <div className="flex flex-col p-8 md:max-h-[90vh] md:overflow-y-auto md:p-10">
                <span className="text-xs uppercase tracking-overline text-electric">{product.category}</span>

                <h2 className="mt-4 font-serif text-4xl font-light leading-tight text-ink">{product.name}</h2>
                <div className="mt-2"><Stars value={avg.rating} count={avg.count} /></div>
                {product.show_produttore && product.produttore && (
                  <p className="mt-1.5 text-xs uppercase tracking-wide text-ink-soft" data-testid="product-produttore">
                    Marca prodotto <span className="font-semibold text-ink">{product.produttore}</span>
                  </p>
                )}
                <div className="mt-4 rounded-xl border border-ink/10 bg-panel" data-testid="description-accordion">
                  <button
                    onClick={() => setDescOpen((v) => !v)}
                    data-testid="description-toggle"
                    aria-expanded={descOpen}
                    className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-ink"
                  >
                    Descrizione
                    <ChevronDown className={`h-4 w-4 transition-transform ${descOpen ? "rotate-180" : ""}`} />
                  </button>
                  {descOpen && (
                    <p className="px-4 pb-4 text-base leading-relaxed text-ink-soft" data-testid="description-content">{product.description}</p>
                  )}
                </div>
                {product.stock != null && (
                  <p className={`mt-3 text-xs font-semibold ${product.stock <= 3 ? "text-amber" : "text-electric"}`} data-testid="product-stock">
                    Pezzi disponibili: {product.stock}
                  </p>
                )}

                <div className="mt-5 border-t border-ink/10 pt-5" data-testid="reviews-block">
                  <h3 className="font-serif text-xl text-ink">Recensioni</h3>
                  {user ? (
                    <form onSubmit={submitReview} className="mt-3 rounded-xl bg-panel p-3">
                      <Stars value={myStars} onRate={setMyStars} />
                      <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Racconta com'è andata…" data-testid="review-comment" className="mt-2 w-full rounded-lg border border-ink/15 bg-cream px-3 py-2 text-sm outline-none focus:border-sage" />
                      <button type="submit" data-testid="review-submit" className="mt-2 rounded-full bg-sage px-4 py-2 text-xs font-semibold text-cream hover:bg-sage-deep">Pubblica</button>
                    </form>
                  ) : (
                    <p className="mt-2 text-sm text-ink-soft">Accedi per lasciare una recensione.</p>
                  )}
                  <div className="mt-3 max-h-40 space-y-3 overflow-y-auto pr-1">
                    {reviews.map((r, i) => (
                      <div key={i} data-testid="review-item">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-ink">{r.user_name}</span>
                          <Stars value={r.stars} size="h-3 w-3" />
                        </div>
                        {r.comment && <p className="text-sm text-ink-soft">{r.comment}</p>}
                      </div>
                    ))}
                    {reviews.length === 0 && <p className="text-sm text-ink-soft">Ancora nessuna recensione.</p>}
                  </div>
                </div>

                {reco.length > 0 && (
                  <div className="mt-5 border-t border-ink/10 pt-5" data-testid="reco-block">
                    <h3 className="font-serif text-xl text-ink">Comprati spesso insieme</h3>
                    <div className="mt-3 space-y-2">
                      {reco.map((r) => (
                        <div key={r.id} className="flex items-center gap-3 rounded-xl border border-ink/10 bg-panel p-2.5" data-testid={`reco-${r.id}`}>
                          <img src={r.image} alt="" className="h-10 w-10 rounded-lg object-cover" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-ink">{r.name}</p>
                            <p className="text-xs text-ink-soft">{euro(r.price)}</p>
                          </div>
                          <button
                            onClick={() => { addItem(r, 1); toast.success(`${r.name} aggiunto al carrello`); }}
                            data-testid={`reco-add-${r.id}`}
                            className="grid h-8 w-8 place-items-center rounded-full bg-ink text-cream transition-transform hover:scale-110 hover:bg-sage"
                            aria-label={`Aggiungi ${r.name}`}
                          >
                            <Plus className="h-4 w-4" strokeWidth={2} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-6 text-2xl font-semibold text-ink">
                  {euro(product.price)}
                </div>

                <div className="mt-auto pt-8">
                  {canShop ? (
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-4 rounded-full border border-ink/15 px-4 py-3">
                        <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="modal-decrease" className="text-ink hover:text-sage"><Minus className="h-4 w-4" strokeWidth={2} /></button>
                        <span className="w-5 text-center font-medium" data-testid="modal-qty">{qty}</span>
                        <button onClick={() => setQty((q) => q + 1)} data-testid="modal-increase" className="text-ink hover:text-sage"><Plus className="h-4 w-4" strokeWidth={2} /></button>
                      </div>
                      <button
                        onClick={() => { addItem(product, qty); onClose(); }}
                        data-testid="modal-add-to-cart"
                        className="flex flex-1 items-center justify-center gap-2 rounded-full bg-ink py-4 text-sm font-semibold text-cream transition-transform hover:scale-[1.03] active:scale-95"
                      >
                        <ShoppingBag className="h-4 w-4" strokeWidth={1.8} /> Aggiungi al carrello
                      </button>
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-sage/40 bg-sage/10 px-5 py-4 text-center" data-testid="modal-coming-soon">
                      <p className="text-sm font-semibold text-ink">Disponibile all'apertura del negozio</p>
                      <p className="mt-0.5 text-xs text-ink-soft">Salvalo nei preferiti con il cuore in alto: ti avviseremo noi.</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
      {product && lightbox && (
        <ImageLightbox images={gallery} index={imgIndex} onClose={() => setLightbox(false)} onNav={(i) => setCurrentImg(gallery[i])} />
      )}
    </AnimatePresence>
  );
};
